/**
 * Time, in the words people use.
 *
 * Nobody reading a collection screen wants to subtract dates. "asked 5 days
 * ago" and "overdue by 3 days" are read at a glance; `2026-08-13T04:11:22Z`
 * has to be worked out, and on a screen with forty rows nobody works out forty.
 */

const DAY = 86_400_000;

/**
 * Whole calendar days since a timestamp — 3 Sept to 5 Sept is 2, whatever the
 * clock reads on either day.
 *
 * Both sides are reduced to a calendar day first, same reasoning as
 * `daysUntil` below: comparing raw elapsed milliseconds made a message sent
 * at 11:11am read as "yesterday" right up until 11:11am struck again, two
 * full calendar days later — an off-by-one that lasted most of the day.
 */
export const daysSince = (iso) => {
  if (!iso) return null;
  const then = new Date(iso);
  const a = Date.UTC(then.getFullYear(), then.getMonth(), then.getDate());
  const now = new Date();
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((b - a) / DAY);
};

/** "today", "yesterday", "5 days ago". */
export function whenText(iso) {
  const d = daysSince(iso);
  if (d === null) return null;
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  return `${d} days ago`;
}

/** `2026-10-03` -> `3 Oct 2026`. */
export function dateText(isoDay) {
  if (!isoDay) return null;
  const [y, m, d] = isoDay.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * Whole days from today to a `YYYY-MM-DD`, positive when it is still ahead.
 *
 * Both sides are reduced to a calendar day first. A due date is a day, not an
 * instant — comparing it against `Date.now()` makes something due at 11pm look
 * overdue to anyone opening the screen after 11pm.
 */
export function daysUntil(isoDay) {
  if (!isoDay) return null;
  const today = new Date();
  const a = Date.UTC(...isoDay.split('-').map((n, i) => (i === 1 ? Number(n) - 1 : Number(n))));
  const b = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((a - b) / DAY);
}

/** "Today", "Yesterday", "13 Aug 2026" — the divider between days in a thread. */
export function dayText(iso) {
  const d = daysSince(iso)
  if (d === null) return ''
  if (d <= 0) return 'Today'
  if (d === 1) return 'Yesterday'
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

/** `in 46d`, `6d late`, `today`. */
export function shortDue(isoDay) {
  const n = daysUntil(isoDay)
  if (n === null) return null
  if (n === 0) return 'today'
  if (n > 0) return `in ${n}d`
  return `${-n}d late`
}

/** Just the clock time — `6:14 pm` — for a chat bubble's corner. */
export function timeText(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit', hour12: true })
}
